import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCart, useProfile } from '../hooks/api-hooks';

/**
 * The header/footer from variant 5a of the design conversation: brand set in
 * the display serif, muted section links, and the account action as a tinted
 * pill. The data hooks below are what bind the shell to the backend.
 */
export function Layout({ children }: { children: ReactNode }) {
  const { session, signOut, configured } = useAuth();
  const cart = useCart();
  const profile = useProfile();
  const itemCount = cart.data?.items.length ?? 0;

  return (
    <div className="app">
      <a className="skip-link" href="#noi-dung-chinh">
        Chuyển đến nội dung chính
      </a>
      <header className="app-header">
        <Link to="/" className="brand" aria-label="Huy TKD AI — Trang chủ">
          huytkd<span aria-hidden="true">_</span>ai
        </Link>

        <nav aria-label="Điều hướng chính">
          <NavLink to="/" end>
            Khóa học
          </NavLink>
          <Link to="/#skill">Skill</Link>
          <Link to="/#workshop">Workshop</Link>
          {session && <NavLink to="/thu-vien">Thư viện</NavLink>}
          {session && <NavLink to="/kich-hoat">Kích hoạt</NavLink>}
          {profile.data?.role === 'ADMIN' && (
            <NavLink to="/admin">Quản trị</NavLink>
          )}
        </nav>

        <div className="header-actions">
          <Link
            to="/gio-hang"
            className="cart-link"
            aria-label={`Giỏ hàng, ${itemCount} sản phẩm`}
          >
            Giỏ <span className="cart-count">{itemCount}</span>
          </Link>
          {session ? (
            <button type="button" className="link-button" onClick={() => void signOut()}>
              Đăng xuất
            </button>
          ) : (
            <Link to="/dang-nhap" className="btn-pill-tint">
              Đăng nhập
            </Link>
          )}
        </div>
      </header>

      <main className="app-main" id="noi-dung-chinh">
        {!configured && (
          <p className="notice">
            Supabase chưa được cấu hình. Sao chép <code>web/.env.example</code> thành{' '}
            <code>web/.env</code> và điền <code>VITE_SUPABASE_URL</code> cùng{' '}
            <code>VITE_SUPABASE_ANON_KEY</code> để bật đăng nhập, giỏ hàng và thanh toán.
          </p>
        )}
        {children}
      </main>

      <footer className="app-footer">
        <span className="brand">huytkd_ai</span>
        <p>Khóa học và skill AI thực hành. Thanh toán một lần, sử dụng lâu dài.</p>
        <nav className="footer-legal" aria-label="Thông tin pháp lý">
          <Link to="/dieu-khoan">Điều khoản</Link>
          <Link to="/quyen-rieng-tu">Quyền riêng tư</Link>
          <Link to="/chinh-sach-hoan-tien">Hoàn tiền</Link>
        </nav>
      </footer>
    </div>
  );
}
